import { Badge, Box, Button, Flex, HStack, Input, Text, VStack } from '@chakra-ui/react';
import { TitleBar } from '@resistapp/client/components/bars/title-bar';
import { ResistomapSelect } from '@resistapp/client/components/forms/resistomap-select';
import { LocalStorageId } from '@resistapp/client/components/shared/local-storage';
import { theme } from '@resistapp/client/components/shared/theme';
import { Loading } from '@resistapp/client/components/states/loading';
import { useToastWithoutDebounce } from '@resistapp/client/hooks/use-resistomap-toast';
import { resistomapOrganisationId } from '@resistapp/common/features';
import { isProviderAdminUser, isResistomapAdminUser } from '@resistapp/client/utils/access';
import { useEffect, useMemo, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import useLocalStorageState from 'use-local-storage-state';
import { useUser } from '../../contexts/use-user-context';
import { useOrganisations, useProjects } from '../../hooks/api';

export function AdminOrganisationsView() {
  const { user } = useUser();
  const { data: organisations, isLoading, error: organisationsError } = useOrganisations();
  const { data: projects, error: projectsError } = useProjects();
  const showToast = useToastWithoutDebounce();
  const [orgToast, setOrganisationCreated] = useLocalStorageState(LocalStorageId.organisationCreated, {
    defaultValue: false,
  });

  useEffect(() => {
    if (!orgToast) {
      return;
    }
    setOrganisationCreated(false);
    showToast({
      title: 'Organisation created',
      description: 'The organisation has been created',
      status: 'success',
      duration: 9000,
    });
  }, [orgToast, setOrganisationCreated, showToast]);

  type Option = { value: string; label: string };
  const [name, setName] = useState('');
  const [demoFilter, setDemoFilter] = useState<Option | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<Option>({ value: '100', label: '100' });

  const demoOptions: Option[] = useMemo(
    () => [
      { value: 'DEMO', label: 'Demo only' },
      { value: 'NON_DEMO', label: 'Non-demo only' },
    ],
    [],
  );
  const pageSizeOptions: Option[] = useMemo(() => ['20', '50', '100'].map(n => ({ value: n, label: n })), []);

  const filtered = useMemo(() => {
    const lower = name.trim().toLowerCase();
    return (organisations || [])
      .filter(o => {
        const nameOk = !lower || o.name.toLowerCase().includes(lower) || String(o.id).includes(lower);
        const demoOk =
          !demoFilter || (demoFilter.value === 'DEMO' ? o.isDemo : demoFilter.value === 'NON_DEMO' ? !o.isDemo : true);
        return nameOk && demoOk;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [organisations, name, demoFilter]);

  const pageN = Number(pageSize.value);
  const start = (page - 1) * pageN;
  const rows = filtered.slice(start, start + pageN);

  const error = organisationsError || projectsError;
  if (error) {
    throw error;
  }

  const canCreateOrganisations = isResistomapAdminUser(user) || isProviderAdminUser(user);

  return (
    <div style={{ marginBottom: theme.spacing[16] }}>
      <TitleBar
        title="Organisations"
        $extraWide={true}
        controls={
          canCreateOrganisations ? (
            <Flex gap={theme.spacing[4]}>
              <Button as={NavLink} to="/organisation/create" alignSelf="center">
                New organisation
              </Button>
            </Flex>
          ) : null
        }
      />
      <VStack align="stretch" spacing={4} padding={theme.spacing[4]}>
        <Flex gap={theme.spacing[3]} wrap="wrap">
          <Input
            placeholder="Organisation name or ID"
            value={name}
            onChange={e => {
              setName(e.target.value);
              setPage(1);
            }}
            bg="white"
            width={{ base: '100%', md: '280px' }}
          />
          <ResistomapSelect<Option, false>
            options={demoOptions}
            value={demoFilter}
            onChange={opt => {
              setDemoFilter(opt as Option | null);
              setPage(1);
            }}
            isClearable
            placeholder="Demo filter"
            chakraStyles={{ control: base => ({ ...base, backgroundColor: 'white', width: '220px' }) }}
          />
        </Flex>

        {isLoading ? (
          <Box padding={2} borderRadius={6} bg="white" boxShadow="sm">
            <Loading />
          </Box>
        ) : filtered.length === 0 ? (
          <Text>No organisations found.</Text>
        ) : (
          <VStack align="stretch" spacing={2}>
            {rows.map(o => {
              const allProjects = o.id === resistomapOrganisationId;
              const projectCount = allProjects ? projects?.length || 0 : o.projectAccesses.length;
              return (
                <Box key={o.id} padding={2} borderRadius={6} bg="white" boxShadow="sm" _hover={{ boxShadow: 'md' }}>
                  <Text as={Link} to={`/admin/organisation/${o.id}`} fontWeight="bold">
                    {o.name} ({o.id})
                  </Text>
                  <HStack spacing={2} mt={1} wrap="wrap">
                    {o.isDemo ? <Badge colorScheme="orange">Demo</Badge> : null}
                    {allProjects ? (
                      <Badge colorScheme="blue">All projects</Badge>
                    ) : (
                      <Badge colorScheme="blue">Projects: {projectCount}</Badge>
                    )}
                  </HStack>
                </Box>
              );
            })}
            <HStack justify="space-between" mt={2} wrap="wrap">
              <Text fontSize="sm">Total: {filtered.length}</Text>
              <HStack>
                <ResistomapSelect<Option, false>
                  options={pageSizeOptions}
                  value={pageSize}
                  onChange={opt => {
                    if (opt) setPageSize(opt);
                  }}
                  placeholder="Page size"
                  chakraStyles={{ control: base => ({ ...base, backgroundColor: 'white', width: '160px' }) }}
                />
                <Button
                  size="sm"
                  onClick={() => {
                    setPage(p => Math.max(1, p - 1));
                  }}
                  isDisabled={page <= 1}
                >
                  Prev
                </Button>
                <Text>Page {page}</Text>
                <Button
                  size="sm"
                  onClick={() => {
                    setPage(p => p + 1);
                  }}
                  isDisabled={rows.length < pageN}
                >
                  Next
                </Button>
              </HStack>
            </HStack>
          </VStack>
        )}
      </VStack>
    </div>
  );
}
